import { ILeasingPlugin } from "./ILeasingPlugin";
import { IUserInfo } from "../DataStorage/IUserInfo";
import { LeasingData } from "./LeasingData";
import { LeaseResult } from "./LeaseResult";
import { AppGlueCorePlugins } from "../../AppGlueCorePlugins";
import { DataStoreQueryCondition, DataStoreQueryConditionType } from "../DataStorage/DataStoreQueryCondition";

export class LeasingPlugin implements ILeasingPlugin {
    ds = AppGlueCorePlugins.DataStoreFactory!.getDataStoreByName<LeasingData>(LeasingData.name);

    async lease(
        user: IUserInfo,
        clientId: string,
        leaseId: string,
        leaseType: string,
        leaseForSeconds?: number,
        notes?: string,
    ): Promise<LeaseResult> {
        let lease = await this.getLease(user, leaseId, leaseType);

        let d = new Date();
        d.setSeconds(d.getSeconds() + (leaseForSeconds ?? 60));

        if (lease) {
            if (lease.clientId === clientId) {
                // extend lease
                lease.notes = notes;
                lease.leaseUntil = d;
                await this.ds!.store(user, lease);
                return new LeaseResult(true, "lease success");
            } else {
                return new LeaseResult(false, `${leaseType}:${leaseId} is already leased by client: ${clientId}`);
            }
        }

        // add lease
        lease = new LeasingData(clientId, leaseId, leaseType, d);

        let storedLease = await this.ds!.store(user, lease);

        // fetch to see if the lease was sucessful
        let leases = await this.ds!.query(user, [
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "leaseId", leaseId),
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "leaseType", leaseType),
            new DataStoreQueryCondition(DataStoreQueryConditionType.GreaterThan, "leaseUntil", new Date()),
        ]);

        if (leases.length === 1) {
            // its us, we can return that we got the lease
            return new LeaseResult(true, "lease success");
        } else {
            let otherLeases = leases.filter((l) => {
                return l.id !== storedLease.id;
            });

            for (let other of otherLeases) {
                if (other.createdOn! < storedLease.createdOn!) {
                    // someone else got it first
                    await this.ds!.delete(user, storedLease.id!);
                    return new LeaseResult(false, `${leaseType}:${leaseId} is already leased by client: ${clientId}`);
                }
            }
        }

        return new LeaseResult(true, "lease success");
    }

    async renewLease(
        user: IUserInfo,
        clientId: string,
        leaseId: string,
        leaseType: string,
        leaseForSeconds?: number,
    ): Promise<LeaseResult> {
        return this.lease(user, clientId, leaseId, leaseType, leaseForSeconds);
    }

    async getLease(user: IUserInfo, leaseId: string, leaseType: string): Promise<LeasingData | undefined> {
        let leases = await this.ds!.query(user, [
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "leaseId", leaseId),
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "leaseType", leaseType),
            new DataStoreQueryCondition(DataStoreQueryConditionType.GreaterThan, "leaseUntil", new Date()),
        ]);

        if (leases.length === 0) return undefined;

        if (leases.length === 1) return leases[0];

        // get oldest active lease and return
        let returnVal = leases[0];

        for (let l of leases) {
            if (l.createdOn! < returnVal.createdOn!) returnVal = l;
        }

        return returnVal;
    }

    async breakLease(user: IUserInfo, clientId: string, leaseId: string, leaseType: string): Promise<LeaseResult> {
        let leases = await this.ds!.query(user, [
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "clientId", clientId),
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "leaseId", leaseId),
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "leaseType", leaseType),
            new DataStoreQueryCondition(DataStoreQueryConditionType.GreaterThan, "leaseUntil", new Date()),
        ]);

        for (let l of leases) {
            await this.ds!.delete(user, l.id!);
        }

        return new LeaseResult(true, "lease broken");
    }

    async isLeasedByMe(user: IUserInfo, clientId: string, leaseId: string, leaseType: string): Promise<boolean> {
        let lease = await this.getLease(user, leaseId, leaseType);

        if (!lease) return false;

        return lease.clientId === clientId;
    }

    async isLeasedByAnyone(user: IUserInfo, leaseId: string, leaseType: string): Promise<boolean> {
        let lease = await this.getLease(user, leaseId, leaseType);

        return lease !== undefined;
    }

    async listCurrentLeases(user: IUserInfo): Promise<LeasingData[]> {
        return this.ds!.query(user, [
            new DataStoreQueryCondition(DataStoreQueryConditionType.LessThan, "leaseUntil", new Date()),
        ]);
    }
}
